import { useState, useEffect, useRef } from 'react';
import { useConnectionStore } from '@ewf/stores/connection';
import { LIFTING_CHAINS } from '@shared/types/enums';
import { type Wallet } from '@ewf/components/ModalUI/WalletSelector';
import { ConnectWC } from '@ewf/components/ConnectWC';
import { ConnectionLoading } from '@ewf/components/ConnectUserWallet/ConnectionLoading';
import { SelectWallet } from './SelectWallet';
import { ConnectionError } from './ConnectionError';
import { ConnectLedger } from '@ewf/components/ConnectLedger';

export type Step = 'select' | 'connectWC' | 'connectLedger' | 'loading' | 'error';

export const ConnectLiftingWallet = ({
  onConnect,
  onClose: onConnectClose,
  goBack,
  network,
}: Props) => {
  const [step, setStep] = useState<Step>('select');
  const [errMsg, setErrMsg] = useState<string>();
  const stepRef = useRef<Step>(step);
  const isSubscribedModalRef = useRef<boolean>(false);
  const walletConnectModal = useConnectionStore((state) => state.walletConnectModal);
  const errorMsg = useConnectionStore((state) => state.errorMsg);

  if (errorMsg) {
    console.log('Error msg: ', errorMsg);
  }

  const onClose = () => {
    onConnectClose();
    setTimeout(() => setStep('select'), 300);
  };

  const goToConnectionMethod = (wallet: Wallet) => {
    if (wallet === 'wallet-connect') {
      setStep('connectWC');
    }

    if (wallet === 'ledger') {
      setStep('connectLedger');
    }
  };

  const connectionError = (msg?: string) => {
    setErrMsg(msg);
    setStep('error');
  };

  useEffect(() => {
    if (!walletConnectModal) return;
    if (isSubscribedModalRef.current) return;
    isSubscribedModalRef.current = true;

    walletConnectModal.subscribeModal((state) => {
      if (!state.open && stepRef.current === 'connectWC') {
        setTimeout(() => {
          if (stepRef.current === 'connectWC') setStep('select');
        }, 300);
      }

      if (!state.open && step === 'connectLedger') {
        setStep('select');
      }
    });
  }, [step, walletConnectModal]);

  useEffect(() => {
    // Ref is used to keep track of step current value inside of setTimeout callback
    stepRef.current = step;
  }, [step]);

  const stepComponents: Record<Step, React.ReactNode> = {
    select: (
      <SelectWallet
        onNext={goToConnectionMethod}
        onClose={onClose}
        network={network}
        goBack={goBack}
      />
    ),
    connectWC: (
      <ConnectWC
        chain={network}
        isConnectingCallback={() => {
          walletConnectModal?.closeModal();
          setStep('loading');
        }}
        onConnectionSuccess={() => onConnect('wc')}
        onConnectionError={connectionError}
      />
    ),
    connectLedger: (
      <ConnectLedger
        chain={network}
        onClose={onClose}
        onConnectionSuccess={() => onConnect('ledger')}
        onConnectionError={connectionError}
      />
    ),
    loading: <ConnectionLoading />,
    error: <ConnectionError onClose={onClose} msg={errMsg} />,
  };

  const renderComponent = () => {
    return stepComponents[step];
  };

  return renderComponent();
};

interface Props {
  network: LIFTING_CHAINS;
  onConnect: (metho: 'wc' | 'ledger') => void;
  onClose: () => void;
  goBack: () => void;
}
